import { useEffect, useState } from "react";
import { useLocalStorage } from "../utils/LocalStorageHook";
import { BASE_URL } from "../App";
import ReactDOMServer from 'react-dom/server'
import {
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Slider,
} from "@mui/material";
import { Theme } from '@mui/material/styles'
import { createStyles, makeStyles } from '@mui/styles';
import { GetTradeChannelACV, tradeChannelACV } from "../utils/GetCustomer"
import Chart, { Bubble } from "react-chartjs-2";
import requestMissingContexts, {
  determineConfig,
  determineConfigLong,
} from "../utils/GetContext";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    centerTitle: {
      textAlign: "center",
      padding: theme.spacing(1),
    },
    fullHeight: {},
    centerDiv: {
      textAlign: "center",
    },
    gridSpaceable: {
      padding: theme.spacing(2),
    },
    cards:{
      height: "100%",
      border:"none",
      boxShadow: "none"
    },
    gridMarginTop: {
    },
  })
);

interface activeHoverObject{
  key: string;
  acv: string;
  brand: string;
  brandText: string;
  color: string;
  flavor: string;
  flavorText: string;
  percent_volume: string;
  text: string;
  x: number;
  y: number;

}
/**
 * Generates a react card that handles building and displaying trade channel view when given a customer number
 * @param props.customerNum The customer that we are using
 * @returns 
 */
export default function CardLocalFavorites(props: { customerNum: string, lfRows?: number, lfCols?: number, curMaterialNums?: string[], newMaterialNums?: string[] }) {
  const classes = useStyles();

  const [salesOrg,] = useLocalStorage("SETTINGS.salesOrg", "");
  const [clientId,] = useLocalStorage("SETTINGS.clientId", "100");
  const [tradeChannelACVs, setTradeChannelACVs] = useState<tradeChannelACV[]|undefined>(undefined);
  const [activeHover, setActiveHover] = useState<activeHoverObject|undefined>(undefined);
  const [daysBack, setDaysBack] = useState<number>(14);
  const [codes, setCodes] = useState<any>();

  const customerNum = props.customerNum;

  useEffect(() => {
    if(!tradeChannelACVs){
      requestMissingContexts([
        "TRADECHANNEL",
        "BRAND",
        "FLAVOUR",
        "vp_flavor_color",
      ]).then((v) => {
        setCodes(v);
      });
      GetTradeChannelACV(salesOrg,customerNum,daysBack).then(tcacv => {
        setTradeChannelACVs(tcacv)
      })
    }
    //vp_flavor_color

  }, [tradeChannelACVs,daysBack]);

  
  const formatPercentage = (num:number) => {
    return `${(Math.round(num * 100) / 100).toFixed(2)}%`
  }

  
  const getImageBubble = (brand:string, flavor:string, size:number) =>{
    let color = determineConfig(codes,"vp_flavor_color",flavor) || "#000000"
    const logoURL = `${BASE_URL}/api/images/logo?brand=${brand}&color=${escape(color)}`
      return {
        logoURL,
        size,
        color
      }
    }

  const getBubbleData = () => {
    let datasets:Chart.ChartDataSets[] = tradeChannelACVs?.map(tc=>{
      const logo = getImageBubble(tc.brand,tc.flavor,(tc.percent_volume*2+tc.acv)) 
      const chartPoint = new Image(logo.size,logo.size)
      chartPoint.src = logo.logoURL 
      //chartPoint.src = logo.uri
      let labelData = {
        brand:tc.brand,
        brandText:determineConfig(codes,"BRAND",tc.brand),
        flavor:tc.flavor,
        flavorText: determineConfig(codes,"FLAVOUR",tc.flavor),
        percent_volume: formatPercentage(tc.percent_volume),
        acv: formatPercentage(tc.acv),
        text: ''
      }
      chartPoint.alt = JSON.stringify(labelData)
      labelData.text = `${labelData.brand}:${labelData.brandText} / ${labelData.flavor}:${labelData.flavorText}`
      return {
        label:labelData.text,
        backgroundColor: logo.color,
        pointStyle: chartPoint,
        data:[{
          x: tc.percent_volume,
          y: tc.acv,
          r: logo.size/2
        }]
      }
    }) || []

    return {
      labels:['Volume','ACV',''],
      datasets
    };
  }

  const opt: Chart.ChartOptions = {
    responsive:true,
    legend:{display:false},
    onHover: (event: MouseEvent, activeElements: any[])=> { 
      let usefulData:any = {}
      if(activeElements.length >0){
        let aem = activeElements[0]._model;
        usefulData = {
          ...JSON.parse(aem.pointStyle.alt||'{}'),
          color:aem.backgroundColor,
          x:aem.x,
          y:aem.y,
        }
        usefulData.key = `${usefulData.brand}-${usefulData.flavor}`
        if(!activeHover || activeHover.key !== usefulData.key){
          setActiveHover(usefulData)
        }
      } else {
        if(activeHover) setActiveHover(undefined)
      }
    },
    tooltips:{
      custom: (tooltipModel:Chart.ChartTooltipModel)=>{
        if (activeHover && tooltipModel){
          tooltipModel.title = [
            `Brand:${'\t'}${activeHover.brandText} / ${activeHover.brand}`,
            `Flavor:${'\t'}${activeHover.flavorText} / ${activeHover.flavor}`,
            `ACV:${'\t'}${activeHover.acv}`,
            `Volume:${'\t'}${activeHover.percent_volume}`,
          ]
          tooltipModel.body = []
          tooltipModel.width = 225
          tooltipModel.height = 65
          tooltipModel.borderColor = activeHover.color
          if(tooltipModel.caretX > tooltipModel.x){
            tooltipModel.x = tooltipModel.caretX - tooltipModel.width
          }
        } else {
          return
        }
      }
    },
    scales: {
      yAxes: [{
          scaleLabel: {
              display: true,
              labelString: 'ACV%'
          }
      }],
      xAxes: [{
          scaleLabel: {
              display: true,
              labelString: 'Volume%'
          },
      }]
  },
  }

  const CUR_TRADE_CHANNEL = (tradeChannelACVs && tradeChannelACVs.length >0)?tradeChannelACVs[0].trade_channel:''


  return (
    <Card className={classes.cards}>
      <CardContent className={classes.fullHeight}>
        <Typography variant="h4" className={classes.centerTitle}>
          Trade Channel View
        </Typography>
        <Grid container spacing={2} className={classes.gridMarginTop}>
          <Grid
              item
              xs={12}
              md={12}
              lg={12}
              className={classes.gridSpaceable}
            >
            <div style ={{paddingLeft:'20px',paddingRight:'20px'}}> 
              <Slider 
                disabled={!tradeChannelACVs}
                defaultValue={daysBack/7}
                value={daysBack/7}
                onChange={(ce,val)=>{ 
                  if (typeof val === "number") 
                    setDaysBack(val*7)
                  else 
                  setDaysBack(val[0]*7)
                }}
                onChangeCommitted={(ce,val)=>{setTradeChannelACVs(undefined)}}
                aria-label="Default" 
                valueLabelDisplay="auto" 
                step={1} 
                min={1} 
                max={9} 
                marks={[
                  {value: 2,label: '2 Weeks',},
                  {value: 4,label: '4 Weeks',},
                  {value: 8,label: '8 Weeks',},
                ]}
                />
            </div>
          </Grid>
        </Grid> 
        <Typography variant="h5" className={classes.centerTitle}>
          {determineConfig(codes,"TRADECHANNEL",CUR_TRADE_CHANNEL)}
        </Typography>
        <Typography variant="body2">
          {determineConfigLong(codes,"TRADECHANNEL",CUR_TRADE_CHANNEL)}
        </Typography>
        <br/>
        {(tradeChannelACVs)?(
          <Bubble 
            id="Channel-View-Bubble"
            options={opt}
            data={getBubbleData()}
          />
        ):(<div className={classes.centerDiv}>
          <CircularProgress color="primary" />
        </div>)}
      </CardContent>
    </Card>
  );
}
